import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { SelectItem, SelectItemGroup } from 'primeng/api';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { BusinessunitModel } from 'projects/core/src/lib/models/businessunit.model';
import { LocationModel } from 'projects/core/src/lib/models/location.model';
import { TemplateModel } from 'projects/core/src/lib/models/template.model';
import { Subject, switchMap } from 'rxjs';
import { OwnObserversChangeEvent } from '../orderform-own-observers/orderform-own-observers.component';
import { TemplateChangeEvent } from '../orderform-template/orderform-template.component';

interface MatchingDate {
  date: string;
  status: string;
  times: Array<string>;
  selected: boolean;
  info: string;
  text?: boolean;
}

export interface MatchingPayload {
  template: number;
  location?: number;
  businessunit: number;
  own_observer?: boolean;
  own_observer_gem?: boolean;
  hr_selection?: string | null;
  tags?: Array<string> | null;
}

@Component({
  selector: 'app-orderform-date',
  templateUrl: './orderform-date.component.html',
  styleUrls: ['./orderform-date.component.scss']
})
export class PlannerOrderformDateComponent implements OnInit, OnDestroy {

  @Input() form: UntypedFormGroup;
  @Input() options: { [key: string]: Array<SelectItem | SelectItemGroup> };
  @Input() texts: { [key: string]: string };
  @Input() disabled = true;

  public loading = false;
  public error = false;
  public submitted = false;

  public dates: Array<MatchingDate> = [];
  public times: Array<SelectItem> = [];

  // config
  private template: TemplateModel = null;
  private location: LocationModel = null;
  private businessunit: BusinessunitModel = null;
  private ownObserverGem = false;
  private ownObserver = false;
  private hrSelection: string | null = null;
  private tags: Array<string> | null = null;

  private subForm: UntypedFormGroup = this.fb.group({
    date: new UntypedFormControl('', [Validators.required]),
    time: new UntypedFormControl('', [Validators.required])
  });

  private matchingLookup$: Subject<MatchingPayload> = new Subject();

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder
  ) { }

  ngOnInit(): void {
    this.initMatchingLookup();
    this.form.addControl('date', this.subForm);
    this.subForm.get('time').disable();
  }

  ngOnDestroy(): void {
    this.matchingLookup$.unsubscribe();
  }

  public setBusinessunit(businessunit: BusinessunitModel): void {
    this.businessunit = businessunit;
  }

  public setTemplate(event: TemplateChangeEvent | null): void {
    this.template = event?.template || null;
    if (event?.location !== null) {
      this.location = event.location;
    }
    this.tags = (event?.tags?.length > 0) ? event.tags : null;
    this.loadMatchings();
  }

  public setObserverMode(event: OwnObserversChangeEvent): void {
    this.ownObserver = !!event.own_observer;
    this.ownObserverGem = !!event.own_observer_gem;
    this.hrSelection = event.hr_selection;
    this.loadMatchings();
  }

  public toggleDate(date: MatchingDate): void {
    const actual = this.dates.find((date: MatchingDate) => {
      return date.selected === true;
    });
    if (actual === date) {
      date.selected = false;
      this.subForm.get('date').setValue('');
      this.subForm.get('time').disable();
      this.times = [];
    } else {
      if (actual !== undefined) {
        actual.selected = false;
      }
      date.selected = true;
      this.subForm.get('date').setValue(date.date);
      this.subForm.get('time').enable();
      this.times = date.times.map((time: string) => {
        return {
          label: time,
          value: time
        };
      });
    }
  }

  public getPayload(): any {
    return {
      date: this.subForm.get('date').value,
      time: this.subForm.get('time').value
    };
  }

  public setSubmitted(): void {
    this.submitted = true;
  }

  private loadMatchings(): void {
    if (this.businessunit?.id && this.template?.id && this.location?.id) {
      const payload: MatchingPayload = {
        template: this.template.id,
        location: this.location.id,
        businessunit: this.businessunit.id,
        own_observer: this.ownObserver,
        own_observer_gem: this.ownObserverGem,
        hr_selection: this.hrSelection,
        tags: this.tags || null
      };
      this.subForm.get('time').setValue('');
      this.subForm.get('time').disable();
      // to avoid parallel requests
      this.matchingLookup$.next(payload);
    } else {
      this.dates = [];
    }
  }

  private initMatchingLookup(): void {
    this.matchingLookup$
      .pipe(switchMap((payload: MatchingPayload) => {
        this.error = false;
        this.loading = true;
        return this.api.getOrderMatchings(payload);
      }))
      .subscribe({
        next: (result: any) => {
          const dates = [];
          if (result.data) {
            Object.keys(result.data).forEach((key: string) => {
              dates.push({
                date: key,
                status: result.data[key].status,
                times: result.data[key].times,
                info: result.data[key].info,
                selected: false,
                text: false
              });
            });
            this.dates = dates.sort((a: MatchingDate, b: MatchingDate) => {
              if (a.status === b.status) {
                return (b.date < a.date) ? 1 : (b.date > a.date) ? -1 : 0;
              }
              return (b.status < a.status) ? 1 : (b.status > a.status) ? -1 : 0;
            });
          }
          // set text on break between dates
          if (this.location.virtual !== true) {
            const firstRequestDate = this.dates.find((date: MatchingDate) => {
              return date.status === 'yellow';
            });
            if (firstRequestDate) {
              firstRequestDate.text = true;
            }
          }
          // set error on empty list
          if (this.dates.length === 0) {
            this.error = true;
          }
          this.loading = false;
        },
        error: (error: any) => {
          this.error = true;
          this.loading = false;
        }
      });
  }

}
